// Which of Settings' tabs the page opens on (01-the-console.md § conventions:
// tabs, a form): its first, unless a former address, a returning proof or a
// form left open in the history entry names another. The Billing tab is the
// one tab with an address of its own, `settings/billing`, which the shell
// turns into the tab's navigation state.

import { formOf } from "../../ui/form.tsx";

/** PLAN_TAB is the Plan tab's id, the navigation state a link to it carries (02-DESIGN/17-plans/02-features-and-limits.md § the console). */
export const PLAN_TAB = "plan";

/**
 * BILLING_TAB is the Billing tab's id (02-DESIGN/18-billing/02-the-billing-page.md):
 * the tab the address `/<slug>/settings/billing` opens, as the former sign-in
 * address opens the Sign-in tab, and the navigation state a link to it carries.
 */
export const BILLING_TAB = "billing";

/** Tab is one of Settings' tabs. */
export type Tab = "workspace" | "sign-in" | "zero-trust" | typeof PLAN_TAB | typeof BILLING_TAB;

/**
 * initialTab is the tab a former address, a returning proof, or a form open
 * in the history entry — a refresh — opens on. A form's name begins with its
 * tab's: `sign-in/set-up`, `zero-trust/present`.
 */
export function initialTab(state: unknown, proof = false): Tab | undefined {
  if (proof) {
    return "sign-in";
  }
  const form = formOf(state);
  if (form?.startsWith("sign-in/") === true) {
    return "sign-in";
  }
  if (form?.startsWith("zero-trust/") === true) {
    return "zero-trust";
  }
  if (form?.startsWith(`${BILLING_TAB}/`) === true) {
    return BILLING_TAB;
  }
  // A link may carry the tab it came from (01-the-console.md § conventions:
  // tabs): Home's *needs you* sends a waiting share to Zero-trust, a redirect
  // from the former sign-in address to Sign-in, a plan that needs an admin
  // and a refusal a plan caused to Plan.
  if (typeof state === "object" && state !== null && "tab" in state) {
    const tab = (state as { tab?: unknown }).tab;
    if (
      tab === "sign-in" ||
      tab === "zero-trust" ||
      tab === "workspace" ||
      tab === PLAN_TAB ||
      tab === BILLING_TAB
    ) {
      return tab;
    }
  }
  return undefined;
}
